import { Component, OnDestroy, OnInit, Optional } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import '@khmyznikov/pwa-install';
import { CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { UpdateService } from './services/admin services/update.service';
import { CommonModule } from '@angular/common';
import { MatDialogModule } from '@angular/material/dialog';
import { CookiesComponent } from './components/cookies/cookies.component';
import { CookiesService } from './services/admin services/cookies.service';
import { SessionManagementService } from './services/admin services/session-management.service';
import { RouterModule } from '@angular/router';
import { UserService } from './services/user services/user.service';
import { AnonymousUserService } from './services/user services/anonymous-user.service';
import { doc, Firestore, getDoc } from '@angular/fire/firestore';
import { PresenceService } from './services/user services/presence.service';
import { DeviceService } from './services/admin services/device.service';
import { DeviceInfo } from './interfaces/master-interfaces';
import { Auth, signInAnonymously } from '@angular/fire/auth';
import { SwUpdate } from '@angular/service-worker';





@Component({
  selector: 'app-root',
  standalone: true,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    CommonModule,
    MatDialogModule,
    CookiesComponent,
    RouterModule,
  ],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})




export class AppComponent implements OnInit, OnDestroy {



  deviceInfo!: DeviceInfo;
  deviceClass!: string;
  isUserAuthenticated = false;
  updateStatus: 'none' | 'checking' | 'available' | 'installing' | 'completed' | 'failed' | 'error' = 'none';
  isLoading = true;
  private destroy$ = new Subject<void>();
  private subscriptions = new Subscription();
  private surveyTimer?: Subscription;
  // SW Update tracking
  private pendingUpdate = false;
  private updateCheckInterval?: any;

  constructor(
    private _deviceService: DeviceService,
    public _updateService: UpdateService,
    private _cookiesService: CookiesService,
    private _userService: UserService,
    private _anonymousUserService: AnonymousUserService,
    private _firestore: Firestore,
    private _presenceService: PresenceService,
    private _auth: Auth,
    private _sessionManagementService: SessionManagementService,
    @Optional() private _swUpdate?: SwUpdate,
  ) {
    this.initializeCookieConsent();
  }

  async ngOnInit() {
    this.deviceInfo = this._deviceService.getDeviceInfo();
    this.setDeviceClass();

    try {
      // Initialize app (loads or creates user)
      await this.initializeApp();

      // ✅ SAFE: Wait for SW ready before initializing update service
      if ('serviceWorker' in navigator) {
        await navigator.serviceWorker.ready;
        // console.log('✅ SW Ready in AppComponent');
      }

      this._updateService.initializeUpdateService();

      // 🔄 Initialize SW update checking (runs in background, no interruptions)
      this.initializeSwUpdateChecking();

      //  console.log('✅ AppComponent initialization complete!');

    } catch (error) {
      console.error('Error during app initialization:', error);
      this.isLoading = false;
      this.updateStatus = 'error';
    }
  }

  private initializeSwUpdateChecking(): void {
    if (!this._swUpdate?.isEnabled) {
      //  console.log('⚠️ SW Update not enabled');
      return;
    }

    // console.log('🔄 Initializing SW update checking...');

    // Check for updates immediately and then every 60 seconds
    this.checkForUpdate();
    this.updateCheckInterval = setInterval(() => this.checkForUpdate(), 60000);
  }

  /**
   * Check for and activate SW updates
   */
  private async checkForUpdate(): Promise<void> {
    try {
      const found = await this._swUpdate?.checkForUpdate();
      if (found) {
        //  console.log('✨ SW Update found, activating...');
        await this._swUpdate?.activateUpdate();
        this.pendingUpdate = true;

      }
    } catch (error) {
      console.warn('⚠️ SW update check failed:', error);
    }
  }

  /**
     * 
   * Step 1: Load existing user from IDB (ONLY current_user, NOT device_id)
   * Step 2: If no user exists, create new user (ONLY current_user, NOT device_id)
   * Step 3: Sync service state
   * Step 4: Verify Firestore sync
   * Step 5: Start presence session
   * 
   */
  async initializeApp(): Promise<void> {
    try {
      // console.log('📋 Step 0: Ensure anonymous authentication');
      if (!this._auth.currentUser) {
        //  console.log('📍 No current user, calling signInAnonymously...');
        await signInAnonymously(this._auth);
        // console.log('📍 signInAnonymously completed');
      }
      const authUid = this._auth.currentUser?.uid;
      console.log('📍 authUid retrieved:', authUid);
      if (!authUid) {
        console.error('❌ Failed to authenticate');
        return;
      }
      // console.log('✅ Authenticated as:', authUid);

      //  console.log('📋 Step 1: Try to get existing user from IDB');
      let currentUser = await this._userService.getCurrentUser();

      if (!currentUser || !currentUser.id || !currentUser.displayName) {
        //  console.log('📝 Step 2: No user found - creating new user with auth uid');

        // console.log('✅ Authenticated as:', authUid);
        // console.log('📝 Step 2: No user found - creating new user with auth uid');
        // console.log('📍 About to call createNewUser with authUid:', authUid);  
        // console.log('📍 authUid at this point:', authUid);
        await this._anonymousUserService.createNewUser(authUid);

        currentUser = await this._userService.getCurrentUser();

        if (!currentUser) {
          console.error('❌ Failed to create user');
          return;
        }
      }

      const userId = currentUser.id;

      //  console.log('📋 Step 3: Sync AnonymousUserService state');
      this._anonymousUserService.currentUserId = userId;
      this._anonymousUserService.userDisplayName = currentUser.displayName;

      if (userId !== currentUser.displayName) {
        console.warn('⚠️ WARNING: userId !== displayName - data inconsistency detected!');
      } else {
        //  console.log('✅ Verified: userId === displayName');
      }

      // console.log('📋 Step 4: Verify Firestore sync');
      const userDocRef = doc(this._firestore, 'anonymous_users', userId);
      const userDocSnap = await getDoc(userDocRef);

      if (!userDocSnap.exists()) {
        console.warn('⚠️ User document does not exist in Firestore - creating it');
        try {
          await this._anonymousUserService.saveUserToFirestore(
            currentUser.lat ?? undefined,
            currentUser.lon ?? undefined
          );
          // console.log('✅ User saved to Firestore');
        } catch (firestoreError) {
          console.error('❌ Failed to save user to Firestore:', firestoreError);
        }
      } else {
        //  console.log('✅ User exists in Firestore:', userId);
      }

      // console.log('📋 Step 5: Update subject and start presence');
      this._anonymousUserService.currentUserSubject.next({
        id: currentUser.id,
        displayName: currentUser.displayName,

        lat: currentUser.lat || null,
        lon: currentUser.lon || null
      });

      // Start presence session (route-agnostic)
      await this._presenceService.startSession(userId);

      // console.log('✅ initializeApp COMPLETED for userId:', userId);
      this.isLoading = false;

    } catch (error) {
      console.error('❌ Error in initializeApp:', error);
      this.isLoading = false;
      // App continues with fallback logic
    }
  }

  private setDeviceClass() {
    this.deviceClass = `device-${this.deviceInfo.type} os-${this.deviceInfo.os}`;
  }

  private initializeCookieConsent() {
    const hasConsent = this._cookiesService.getCookie('cookiesAccepted');

    if (hasConsent) {
      this.initializeAnalytics();
    }
  }

  private initializeAnalytics() {
    if (this._cookiesService.getCookie('cookiesAccepted')) {
      // Your analytics initialization code
    }
  }

  clearCookies(): void {
    document.cookie.split(";").forEach(cookie => {
      document.cookie = cookie
        .replace(/^ +/, "")
        .replace(/=.*/, "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/");
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.subscriptions.unsubscribe();
    if (this.surveyTimer) {
      this.surveyTimer.unsubscribe();
    }
    this.subscriptions.unsubscribe();
  }
}